import { useEffect } from 'react';
import type { Layer, LayerOptions, InteractiveLayerOptions } from '@anlan/gs-map';
import { useAttribution } from './attribution';
import { useLeafletContext } from './context';
import { useEventHandlers } from './events';
import { withPane } from './pane';

import type { LeafletContextInterface } from './context';
import type { ElementHook, LeafletElement } from './element';
import type { EventedProps } from './events';

export interface LayerProps extends EventedProps, LayerOptions {
    attribution?: string | null
}
export interface InteractiveLayerProps extends LayerProps, InteractiveLayerOptions {}

export function useLayerLifecycle(element: LeafletElement<Layer>, context: LeafletContextInterface) {
    useEffect(function addLayer() {
        const container = context.layerContainer ?? context.map;
        container.addLayer(element.instance);
        return function removeLayer() {
            context.layerContainer?.removeLayer(element.instance);
            context.map.removeLayer(element.instance);
        };
    }, [
        context,
        element
    ]);
}
export function createLayerHook<E extends Layer, P extends LayerProps, C = unknown>(useElement: ElementHook<E, P, C>) {
    return function useLayer(props: P) {
        const context = useLeafletContext();
        const elementRef = useElement(withPane(props, context), context);
        useAttribution(context.map, props.attribution);
        useEventHandlers(elementRef.current, props.eventHandlers);
        useLayerLifecycle(elementRef.current, context);
        return elementRef;
    };
}
